
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';

interface EReceipt {
  order_id: string;
  email: string;
  sent_at: string;
  status: 'sent' | 'failed';
}

interface EReceiptsData {
  enabled: boolean;
  history: EReceipt[];
}

export const EReceiptsPage: React.FC = () => {
  const [data, setData] = useState<EReceiptsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggleSaving, setToggleSaving] = useState(false);
  const [resending, setResending] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/madebb/shop-admin/ereceipts');
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
      const jsonData: EReceiptsData = await res.json();
      setData(jsonData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load eReceipts data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async () => {
    if (!data) return;
    setToggleSaving(true);
    try {
      const res = await authFetch('/api/madebb/shop-admin/ereceipts/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !data.enabled }),
      });
      if (!res.ok) {
        throw new Error('Failed to toggle eReceipts');
      }
      const updatedData = await res.json();
      setData(d => d ? { ...d, enabled: updatedData.enabled } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setToggleSaving(false);
    }
  };

  const handleResend = async (order_id: string) => {
    setResending(order_id);
    try {
      const res = await authFetch(`/api/madebb/shop-admin/ereceipts/${order_id}/resend`, {
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error(`Failed to resend for order ${order_id}`);
      }
      // Optionally, refresh data or show a success message
    } catch (err) {
      setError(err instanceof Error ? err.message : `An error occurred while resending for ${order_id}`);
    } finally {
      setResending(null);
    }
  };

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <RouterLink to="/madebb/inventory" style={{ textDecoration: 'none', color: 'inherit' }}>
          Made.bb
        </RouterLink>
        <Typography color="text.primary">eReceipts</Typography>
      </Breadcrumbs>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        eReceipt Management
      </Typography>

      {loading && <CircularProgress />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {data && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={data.enabled}
                  onChange={handleToggle}
                  disabled={toggleSaving}
                  data-testid="ereceipt-toggle"
                />
              }
              label={data.enabled ? 'eReceipts Enabled' : 'eReceipts Disabled'}
            />
          </Paper>

          <Typography variant="h6" fontWeight={600} gutterBottom>
            History
          </Typography>

          <TableContainer component={Paper}>
            <Table data-testid="ereceipt-history-table">
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.history.map((receipt) => (
                  <TableRow key={receipt.order_id}>
                    <TableCell>{receipt.order_id}</TableCell>
                    <TableCell>{receipt.email}</TableCell>
                    <TableCell>{new Date(receipt.sent_at).toLocaleString()}</TableCell>
                    <TableCell>{receipt.status}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleResend(receipt.order_id)}
                        disabled={resending === receipt.order_id}
                        data-testid={`resend-btn-${receipt.order_id}`}
                      >
                        {resending === receipt.order_id ? 'Sending...' : 'Resend'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
